import { SxProps } from '@mui/material';

/**
 * Design tokens for the dashboard.
 *
 * Direction: instrument panel, not marketing page. This screen answers "is the platform healthy
 * today, and is anything waiting on me?", so it reads like telemetry — quiet surfaces, one accent
 * family, and numbers that line up in columns.
 *
 * Everything structural (surface, border, text) comes from the MUI palette so both themes keep
 * working. Only the chart accents are fixed hex, because a chart series needs a stable identity
 * across themes.
 */

/**
 * System mono, deliberately not a webfont: HQ is an internal tool and a Google Fonts request is a
 * network dependency it does not need. On macOS this resolves to SF Mono.
 */
export const MONO: string =
    'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

/**
 * Every figure on the dashboard uses this. Tabular figures are the whole point — proportional
 * digits make a column of numbers impossible to scan, which is most of why the old tables looked
 * unfinished.
 */
export const numeric: SxProps = {
    fontFamily: MONO,
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
};

/** Small uppercase category label sitting above a card title or beside a figure. */
export const eyebrow: SxProps = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'text.secondary',
    lineHeight: 1.4,
};

/**
 * Chart series colours. Indigo / teal / amber: distinguishable for the common forms of colour
 * blindness, and all three hold their identity against both a white and a near-black surface,
 * which MUI's default blue/orange/red did not.
 */
export const series = {
    request: '#5B7CFA',
    response: '#12A594',
    runs: '#E0A33E',
} as const;

const tokenFormatter: Intl.NumberFormat = new Intl.NumberFormat('en-GB');

/** 38035388 -> "38,035,388" */
export const formatTokens: (value: number) => string = (value: number): string => {
    return tokenFormatter.format(value ?? 0);
}

const compactFormatter: Intl.NumberFormat = new Intl.NumberFormat('en-GB', {
    notation: 'compact',
    maximumFractionDigits: 1,
});

/** 38035388 -> "38M". For figures where magnitude matters more than the exact value. */
export const formatCompact: (value: number) => string = (value: number): string => {
    return compactFormatter.format(value ?? 0);
}

const timeFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
});

/** Time only. The card is titled "today", so repeating the date on all 325 rows is noise. */
export const formatClock: (date: Date) => string = (date: Date): string => {
    return timeFormatter.format(date);
}

/** Seconds as a duration: "0.84s", "12.3s", "2m 05s". */
export const formatDuration: (seconds: number) => string = (seconds: number): string => {
    if (seconds === undefined || seconds === null || Number.isNaN(seconds)) {
        return '—';
    }
    if (seconds < 10) {
        return seconds.toFixed(2) + 's';
    }
    if (seconds < 60) {
        return seconds.toFixed(1) + 's';
    }
    const mins: number = Math.floor(seconds / 60);
    const rest: number = Math.round(seconds % 60);
    return mins + 'm ' + String(rest).padStart(2, '0') + 's';
}

/**
 * Money.
 *
 * Two decimal places is right for a figure somebody would put in a spreadsheet, and wrong for a
 * day that cost less than a penny: a real spend of $0.0019 rendering as "$0.00" reads as "nothing
 * ran today". Below a cent, four places, so a small number still looks like a number.
 */
const moneyFormatters: Record<string, Intl.NumberFormat> = {};

const moneyFormatter: (currency: string, places: number) => Intl.NumberFormat = (
    currency: string,
    places: number
): Intl.NumberFormat => {
    const key: string = currency + ':' + places;
    if (moneyFormatters[key] === undefined) {
        moneyFormatters[key] = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: places,
            maximumFractionDigits: places,
        });
    }
    return moneyFormatters[key];
}

/**
 * How many decimal places a figure needs: two, or four when it would otherwise round to nothing.
 *
 * Exported because a USD figure and its GBP conversion must agree. Left to decide for themselves
 * they disagree exactly where the rule bites: $0.0116 converts to £0.0091, so the dollars round to
 * "US$0.01" while the pounds keep four places, and one amount ends up printed to two precisions
 * side by side as though they were different quantities.
 */
export const moneyPlaces: (value: number) => number = (value: number): number => {
    const amount: number = value ?? 0;
    return (amount !== 0 && Math.abs(amount) < 0.01) ? 4 : 2;
}

const formatMoney: (value: number, currency: string, places?: number) => string = (
    value: number,
    currency: string,
    places?: number
): string => {
    const amount: number = value ?? 0;
    return moneyFormatter(currency, places ?? moneyPlaces(amount)).format(amount);
}

/**
 * 5 -> "US$5.00", 0.0019 -> "US$0.0019".
 *
 * Pass `places` to pin the precision, which is what a caller showing USD and GBP together does.
 */
export const formatUsd: (value: number, places?: number) => string = (
    value: number,
    places?: number
): string => {
    return formatMoney(value, 'USD', places);
}

/** 4 -> "£4.00". Converted by the API at the rate it reports beside the figure. */
export const formatGbp: (value: number, places?: number) => string = (
    value: number,
    places?: number
): string => {
    return formatMoney(value, 'GBP', places);
}
